<template>
  <div class="lRegister">
    <LightVariation :button="headerButton" />

    <main class="lRegister__main">
      <section class="lRegister__main-steps">
        <CheckoutLightStep label="Identificação" :is-active="true" />
        <div class="lRegister__main-steps--divider" />
        <CheckoutLightStep label="Pagamento" :is-active="false" />
      </section>
      <fieldset>
        <FormLegend title="Crie sua conta para iniciar sua assinatura" />
        <BaseForm
          :schema="schema"
          :submit="submit"
          submit-button-label="Criar conta"
          has-floating-label
          has-icon-message
        />
      </fieldset>
    </main>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import BaseForm from '@/components/BaseForm';
import { useToast } from 'vue-toastification';
import form from '@/Utils/defaultFormSettings.js';
import useNavigate from '@/composables/useNavigate';
import FormLegend from '@/components/form/FormLegend';
import useDatalayer from '@/composables/useDatalayer';
import { utils as utilsMixins } from '@/mixins/utils';
import useErrorMessages from '@/composables/useErrorMessages';
import LightVariation from '@/components/header/LightVariation';
import CheckoutLightStep from '@/components/checkout/CheckoutLightStep';

export default {
  name: 'LightRegister',
  components: { BaseForm, CheckoutLightStep, FormLegend, LightVariation },
  setup() {
    const userID = ref('');
    const store = useStore();
    const toast = useToast();
    const navigate = useNavigate();
    const datalayer = useDatalayer();
    const errorMessage = useErrorMessages();
    const headerButton = { route: { name: 'Login' }, label: 'Entrar' };
    const ERRORS_REDIRECT_TO_LOGIN = ['SUBSCRIPTION_IS_ON_TRIAL', 'SUBSCRIPTION_ALREADY_ACTIVE'];
    const filledUserEmail = computed(() => navigate.getQueryParam('userEmail') || '');
    const PROCEED_TO_SUBSCRIPTION_ERROR = 'PROCEED_TO_SUBSCRIPTION';

    const schema = computed(() => [
      { ...form.FULLNAME, placeholder: '', colorMode: 'light' },
      { ...form.PHONE, placeholder: '', colorMode: 'light' },
      { ...form.EMAIL, placeholder: '', colorMode: 'light', value: filledUserEmail.value },
      { ...form.PASSWORD, placeholder: '', colorMode: 'light' },
      { ...form.LGPD, colorMode: 'light' },
    ]);

    const goToPaymentStep = async (isNewAccount = false) => {
      if (!isNewAccount) {
        await navigate.removeQueryParam('cf');
      }

      navigate.to('ShortCheckoutPaymentDeprecated', { userID: userID.value });
    };

    const shouldRedirectToPayment = (response) => {
      try {
        return (
          response?.proceedToSubscription ||
          response?.data?.proceedToSubscription ||
          response.error == PROCEED_TO_SUBSCRIPTION_ERROR
        );
      } catch (error) {
        return false;
      }
    };

    const handleErrorResponse = (fullError, email) => {
      const { error, error_plain, data } = fullError;

      if (shouldRedirectToPayment(fullError)) {
        userID.value = data?.user?.userID;

        const isExistingAccountError = error === PROCEED_TO_SUBSCRIPTION_ERROR;
        toast.success(isExistingAccountError ? errorMessage.getValue(PROCEED_TO_SUBSCRIPTION_ERROR) : error_plain);

        goToPaymentStep();
      } else if (ERRORS_REDIRECT_TO_LOGIN.includes(error)) {
        // Note: Maybe here it would be necessary to send a login event so that we know that the user was redirected to login
        toast.success(`Você será redirecionado à página de Login! ${error_plain}`);
        setTimeout(() => navigate.to('Login', { email }), 5000);
      } else {
        toast.error(errorMessage.getValue(error));
      }
    };

    const submit = async (formData) => {
      try {
        const [firstname, ...complementName] = formData.name.trim().split(' ');
        const url = window.location.href;

        const hasFBC = utilsMixins.methods.getCookie('_fbc');
        const hasFBP = utilsMixins.methods.getCookie('_fbp');
        const convertedPhoneNumber = formData.phone.trim()?.replace(/\D/gm, '');
        if (convertedPhoneNumber.length !== 10 && convertedPhoneNumber.length !== 11) {
          return;
        }
        const registerData = {
          firstname,
          lastname: complementName.join(' '),
          phone: formData.phone,
          email: formData.email.toLowerCase(),
          password: formData.password,
          meta: {
            marketingConsent: formData.lgpd,
            url: utilsMixins.methods.hasPMP(url) ? url : utilsMixins.methods.addDefaultPMP(url),
            fbc: hasFBC,
            fbp: hasFBP,
          },
        };

        const [error, data] = await store.dispatch('subscription/userRegister', { ...registerData });

        store.dispatch('subscription/setRegisterFilledFields', {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        });

        if (error) {
          handleErrorResponse(error, registerData.email);
        } else {
          toast.success('Cadastro criado com sucesso!');
          userID.value = data?.user?.userID;
          datalayer.createLightCheckoutEvent(2);
          goToPaymentStep(true);
        }
      } catch (error) {
        console.log('## GPRIM ## error', error);
        toast.error(errorMessage.getValue());
      }
    };

    onMounted(() => {
      navigate.addQueryParams({ cf: 1 });
      datalayer.createLightCheckoutEvent(1);
    });

    return { headerButton, schema, submit };
  },
};
</script>

<style lang="scss" scoped>
.lRegister {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: $white;

  &__main {
    flex: 1;
    padding: 16px;
    background: url('@/assets/img/checkout/light_mobile_background.png') no-repeat right center;
    background-color: $white;

    &-steps {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin: 32px 0 32px;

      &--divider {
        flex: 1;
        border: 0.5px dashed $dark-60;
      }
    }

    > fieldset {
      > legend {
        padding-left: 0.25rem;
        margin-bottom: 32px;
      }

      > form :deep(.grp-button-main) {
        border: none;
        background-color: $seer-stone;
        color: $white;

        &:disabled {
          opacity: 0.4;
        }
      }
    }
  }
}

@media (min-width: 768px) {
  .lRegister {
    &__main {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background: url('@/assets/img/checkout/light_tablet_background.png') no-repeat right center;
      background-color: $white;

      > section,
      fieldset {
        width: 65%;

        > legend {
          max-width: unset;
        }
      }
    }
  }
}

@media (min-width: 1440px) {
  .lRegister {
    &__main {
      justify-content: flex-start;
      padding-top: 40px;
      background: url('@/assets/img/checkout/light_desktop_background.png') no-repeat right center;
      background-color: $white;

      > section {
        width: 176px;
      }

      > fieldset {
        width: 384px;

        > legend {
          max-width: 80%;
          font-family: 'Proxima Nova Bold', Verdana, Tahoma, sans-serif;
          text-align: center;
        }
      }
    }
  }
}
</style>
